import { alpha } from '@mui/material/styles';
import type { TooltipProps } from '@mui/material/Tooltip';
import {
  ANALYTICS_CARD_BORDER_SOFT,
  ANALYTICS_CARD_SHADOW,
  ANALYTICS_DIVIDER,
  ANALYTICS_SURFACE,
  ANALYTICS_TEXT_MUTED,
  ANALYTICS_TEXT_PRIMARY,
} from '../Analytics/analyticsColors';
import {
  ANALYTICS_BODY_FONT_FAMILY,
  ANALYTICS_TITLE_FONT_FAMILY,
  APP_BODY_FONT_FAMILY,
} from '../Analytics/chartTypography';
import { appPalette, componentPalette, neutralPalette } from '../../theme/palette';
import { fontFamilies, fontSizes, fontWeights, letterSpacing } from '../../theme/typography';
import { trackerToolbarButtonTypographySx } from '../TrackerFilter/trackerFilterToolbarStyles';
import { queryAdderTypeScale } from '../../shared/constants/queryAdderPalette';

type QueryAdderUi = Record<string, any>;
type TopicLinkingPanelArgs = {
  active: boolean;
  ui: QueryAdderUi;
};

export const queryAdderPopupPaperSx = {
  borderRadius: '20px',
  overflow: 'hidden',
  border: `1px solid ${ANALYTICS_CARD_BORDER_SOFT}`,
  boxShadow: ANALYTICS_CARD_SHADOW,
  backgroundColor: ANALYTICS_SURFACE,
} as const;

export const queryAdderPopupHeaderSx = {
  px: 3,
  py: 1.5,
  borderBottom: `1px solid ${ANALYTICS_DIVIDER}`,
} as const;

export const queryAdderPopupContentSx = {
  px: 3,
  pt: 3,
  pb: 3,
};

export const queryAdderPopupActionsSx = {
  px: 3,
  py: 1.75,
  borderTop: `1px solid ${ANALYTICS_DIVIDER}`,
} as const;

export const queryAdderPopupFormSpacing = 2.5;

export const queryAdderPopupIconSx = {
  width: 48,
  height: 48,
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

export const queryAdderPopupEyebrowSx = {
  fontFamily: ANALYTICS_TITLE_FONT_FAMILY,
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.24em',
  color: ANALYTICS_TEXT_PRIMARY,
} as const;

export const queryAdderPopupTitleSx = {
  mt: 1,
  fontFamily: ANALYTICS_TITLE_FONT_FAMILY,
  fontSize: '24px',
  fontWeight: 600,
  letterSpacing: '-0.05em',
  lineHeight: 1.15,
  color: ANALYTICS_TEXT_PRIMARY,
} as const;

export const queryAdderPopupDescriptionSx = {
  mt: 1.5,
  maxWidth: 560,
  fontFamily: ANALYTICS_BODY_FONT_FAMILY,
  fontSize: '16px',
  lineHeight: 1.5,
  color: ANALYTICS_TEXT_MUTED,
} as const;

export const queryAdderPopupFieldLabelSx = {
  mt: 1,
  mb: 1,
  fontFamily: '"Open Sans", Arial, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#111111',
} as const;

export const queryAdderPopupInputSx = {
  '& .MuiInputLabel-root': {
    fontFamily: '"Open Sans", Arial, sans-serif',
    color: '#111111',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#111111',
  },
  '& .MuiOutlinedInput-root': {
    minHeight: 48,
    borderRadius: '10px',
    backgroundColor: ANALYTICS_SURFACE,
    fontSize: queryAdderTypeScale.bodyText,
    fontFamily: fontFamilies.body,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DADCE0',
  },
  '& .MuiInputBase-input': {
    padding: '12px 14px',
    fontFamily: fontFamilies.body,
    fontSize: queryAdderTypeScale.bodyText,
  },
  '& .MuiInputBase-inputMultiline': {
    lineHeight: 1.7,
  },
} as const;

export const queryAdderPopupActionButtonSx = {
  ...trackerToolbarButtonTypographySx,
  minWidth: 96,
  height: 32,
  borderRadius: '8px',
  px: 1.5,
  textTransform: 'none',
};

export const adderModalPaperSx = (logColor: string) => ({
  ...queryAdderPopupPaperSx,
  borderTop: `4px solid ${logColor}`,
});

export const adderModalHeaderSx = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  backgroundColor: neutralPalette.white,
  minHeight: 68,
  borderBottom: `1px solid ${ANALYTICS_DIVIDER}`,
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
};

export const adderHeaderTitleGroupSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  minWidth: 0,
  flex: 1,
};

export const adderHeaderIconTileSx = (logColor: string) => ({
  ...queryAdderPopupIconSx,
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(logColor, 0.1),
  color: logColor,
  '& .MuiSvgIcon-root': { fontSize: 20 },
});

export const adderHeaderTitleSx = {
  ...queryAdderPopupTitleSx,
  mt: 0,
  fontFamily: fontFamilies.title,
  fontSize: fontSizes.h6,
  fontWeight: fontWeights.semibold,
  letterSpacing: letterSpacing.label,
  lineHeight: 1.6,
  color: '#111111',
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const adderHeaderActionsSx = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
};

export const adderPrimaryActionButtonSx = {
  ...queryAdderPopupActionButtonSx,
  px: 2,
  fontWeight: 700,
  boxShadow: '0 1px 2px rgba(16,24,40,0.10)',
  backgroundColor: appPalette.primary.main,
  color: appPalette.primary.contrastText,
  '&:hover': {
    backgroundColor: '#155BB5',
  },
  '& .MuiButton-startIcon svg': { fontSize: 17, fontWeight: 700 },
};

export const adderCancelButtonSx = {
  ...queryAdderPopupActionButtonSx,
  px: 2,
  fontWeight: 400,
  borderColor: componentPalette.button.border,
  color: neutralPalette.black,
  backgroundColor: neutralPalette.white,
  boxShadow: '0 1px 2px rgba(16,24,40,0.08)',
  '&:hover': {
    borderColor: componentPalette.button.border,
    backgroundColor: neutralPalette.surfaceRaised,
  },
  '& .MuiButton-startIcon svg': { fontSize: 17 },
};

export const adderSectionCardSx = (ui: QueryAdderUi) => ({
  border: `1px solid ${alpha(ui.cardBorder || ui.divider, 0.7)}`,
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: ui.white,
  boxShadow: 'none',
});

export const adderSectionHeaderSx = (ui: QueryAdderUi) => ({
  px: 2.5,
  py: 1.1,
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
  backgroundColor: neutralPalette.surfaceRaised,
  borderBottom: `1px solid ${alpha(ui.divider, 0.7)}`,
});

export const adderSectionDividerSx = (ui: QueryAdderUi) => ({
  borderBottom: `1px solid ${ui.divider}`,
});

export const adderSectionBodySx = {
  px: { xs: 2.25, sm: 2.5 },
  py: 2,
};

export const adderSectionIconColor = '#98A2B3';

export const adderSectionIconTileSx = {
  width: 30,
  height: 30,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: neutralPalette.white,
  border: `1px solid ${alpha(neutralPalette.textSecondary, 0.2)}`,
  color: adderSectionIconColor,
  flexShrink: 0,
};

export const adderSectionIconSx = {
  fontSize: 18,
};

export const queryAdderTooltipTitleSx = {
  fontFamily: APP_BODY_FONT_FAMILY,
  fontSize: '12px',
  lineHeight: 1.45,
  fontWeight: 400,
  color: neutralPalette.black,
};

export const queryAdderTooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: neutralPalette.white,
      color: neutralPalette.black,
      px: 1.25,
      py: 0.75,
      borderRadius: '8px',
      boxShadow: neutralPalette.shadowSoft,
      border: `1px solid ${neutralPalette.borderSubtle}`,
      maxWidth: 280,
    },
  },
  arrow: {
    sx: {
      color: neutralPalette.white,
    },
  },
};

export const queryAdderTooltipProps: Pick<
  TooltipProps,
  'placement' | 'arrow' | 'enterDelay' | 'leaveDelay' | 'disableInteractive' | 'PopperProps' | 'slotProps'
> = {
  placement: 'top',
  arrow: true,
  enterDelay: 220,
  leaveDelay: 90,
  disableInteractive: true,
  PopperProps: { disablePortal: true },
  slotProps: queryAdderTooltipSlotProps,
};

export const topicLinkingPanelSx = ({ active, ui }: TopicLinkingPanelArgs) => ({
  border: `1px solid ${active ? alpha(appPalette.primary.main, 0.26) : ui.divider}`,
  backgroundColor: active ? alpha(appPalette.primary.main, 0.06) : neutralPalette.surfacePage,
  borderRadius: '12px',
  px: 2.25,
  py: 2,
  cursor: 'pointer',
  '&:focus-visible': {
    outline: `2px solid ${active ? appPalette.primary.main : alpha(appPalette.primary.main, 0.45)}`,
    outlineOffset: '2px',
  },
});

export const utilityModalPaperSx = {
  ...queryAdderPopupPaperSx,
  border: `1px solid ${ANALYTICS_CARD_BORDER_SOFT}`,
  boxShadow: ANALYTICS_CARD_SHADOW,
  backgroundColor: neutralPalette.white,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '84vh',
};

export const utilityModalDialogSx = {
  '& .MuiDialog-container': {
    alignItems: 'center',
    p: { xs: 1.5, sm: 2.5 },
  },
  '& .MuiDialog-paper': {
    m: 0,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(17, 17, 17, 0.24)',
    backdropFilter: 'blur(1px)',
  },
};

export const utilityModalHeaderSx = {
  ...queryAdderPopupHeaderSx,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 2,
  flex: '0 0 auto',
  borderBottom: `1px solid ${ANALYTICS_DIVIDER}`,
  backgroundColor: neutralPalette.white,
};

export const utilityModalIconTileSx = {
  ...queryAdderPopupIconSx,
  backgroundColor: neutralPalette.surfacePage,
  color: appPalette.primary.main,
  '& .MuiSvgIcon-root': { fontSize: 20 },
};

export const utilityModalEyebrowSx = {
  ...queryAdderPopupEyebrowSx,
  color: neutralPalette.textSecondary,
};

export const utilityModalTitleSx = {
  ...queryAdderPopupTitleSx,
  color: neutralPalette.textPrimary,
};

export const utilityModalSubtitleSx = {
  ...queryAdderPopupDescriptionSx,
  color: neutralPalette.textSecondary,
};

export const utilityModalFooterSx = {
  ...queryAdderPopupActionsSx,
  flex: '0 0 auto',
  borderTop: `1px solid ${ANALYTICS_DIVIDER}`,
};

export const utilityModalContentSx = {
  ...queryAdderPopupContentSx,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
};

export const utilityPrimaryActionButtonSx = adderPrimaryActionButtonSx;
export const utilityCancelButtonSx = adderCancelButtonSx;
export const utilitySecondaryActionButtonSx = utilityCancelButtonSx;
